/**
 * A tabela das caixas de conferência das importações em PDF, com os dois totais.
 *
 * Papel: reproduz o `.tbl-wrap` de 360 px com rolagem (l. 846, 877, 909) — a
 * marca de 32 px, as colunas do meio, o valor editável de 110 px com máscara
 * de centavos — e a linha de baixo com "Total selecionado" e "Total impresso no
 * PDF" (l. 851–854).
 *
 * Como usar: a caixa guarda as linhas (`LinhaDaConferencia`) e as recebe de
 * volta por `onMudar` a cada marca e a cada tecla; as células do meio são dela,
 * por `celulas`, porque cada importação mostra colunas diferentes. O total
 * selecionado é refeito daqui a cada mudança. O total impresso zero ou ausente
 * aparece como "—", como o `d.totalPdf?fmt(d.totalPdf):'—'` da origem.
 */
import { type ReactNode } from 'react';

import { mascararCentavos } from '@/shared/lib/mascara';
import { cn } from '@/shared/lib/utils';
import { type LinhaDaConferencia, totalSelecionado } from '../conferencia';
import { formatarReais } from '../formato';
import { CLASSE_DO_CAMPO_SOLTO } from './kit/campo';
import { Celula, CelulaDeCabecalho, LinhaDaTabela, Tabela } from './kit/tabela';

interface TabelaDeConferenciaProps {
  /** Os títulos das colunas entre a marca e o valor. */
  colunas: readonly string[];
  /** O título da coluna do valor, como "Valor Líquido (R$)". */
  colunaDoValor: string;
  linhas: readonly LinhaDaConferencia[];
  /** O nome de cada linha, que dá nome à marca e ao campo de valor. */
  rotuloDaLinha: (indice: number) => string;
  /** As células do meio de uma linha, uma por título de `colunas`. */
  celulas: (indice: number) => ReactNode;
  /** O total impresso no PDF; nulo ou zero aparece como "—". */
  totalPdf: number | null;
  onMudar: (linhas: LinhaDaConferencia[]) => void;
}

/**
 * A tabela de conferência e os dois totais.
 *
 * @param colunas - Os títulos das colunas do meio.
 * @param colunaDoValor - O título da coluna do valor.
 * @param linhas - As marcas e os valores como estão na tela.
 * @param rotuloDaLinha - O nome acessível de cada linha.
 * @param celulas - As células do meio de cada linha.
 * @param totalPdf - O total que o PDF imprime.
 * @param onMudar - Recebe as linhas depois de cada marca e de cada tecla.
 * @returns A tabela com rolagem e a linha dos totais.
 */
export function TabelaDeConferencia({
  colunas,
  colunaDoValor,
  linhas,
  rotuloDaLinha,
  celulas,
  totalPdf,
  onMudar,
}: TabelaDeConferenciaProps) {
  /**
   * Troca uma linha.
   *
   * @param indice - A linha.
   * @param mudanca - A marca ou o valor novo.
   */
  function handleMudar(indice: number, mudanca: Partial<LinhaDaConferencia>) {
    onMudar(
      linhas.map((linha, atual) => (atual === indice ? { ...linha, ...mudanca } : linha)),
    );
  }

  return (
    <>
      <div className="max-h-[360px] overflow-auto">
        <Tabela>
          <thead>
            <tr>
              <CelulaDeCabecalho className="w-8" />
              {colunas.map((coluna) => (
                <CelulaDeCabecalho key={coluna}>{coluna}</CelulaDeCabecalho>
              ))}
              <CelulaDeCabecalho alinhamento="direita">{colunaDoValor}</CelulaDeCabecalho>
            </tr>
          </thead>
          <tbody>
            {linhas.map((linha, indice) => (
              // As linhas não mudam de lugar nem de quantidade enquanto a caixa está aberta.
              <LinhaDaTabela key={indice}>
                <Celula>
                  <input
                    type="checkbox"
                    aria-label={`Incluir ${rotuloDaLinha(indice)}`}
                    checked={linha.marcada}
                    onChange={(evento) =>
                      handleMudar(indice, { marcada: evento.target.checked })
                    }
                  />
                </Celula>
                {celulas(indice)}
                <Celula tipo="numero">
                  <input
                    type="text"
                    inputMode="decimal"
                    aria-label={`Valor — ${rotuloDaLinha(indice)}`}
                    value={linha.valor}
                    onChange={(evento) =>
                      handleMudar(indice, { valor: mascararCentavos(evento.target.value) })
                    }
                    className={cn(CLASSE_DO_CAMPO_SOLTO, 'w-[110px] text-right')}
                  />
                </Celula>
              </LinhaDaTabela>
            ))}
          </tbody>
        </Tabela>
      </div>
      <div className="mt-2.5 flex justify-end gap-4 text-[13px]">
        <span>
          Total selecionado: <strong>{formatarReais(totalSelecionado(linhas))}</strong>
        </span>
        <span className="text-texto-fraco">
          Total impresso no PDF:{' '}
          <strong>
            {totalPdf === null || totalPdf === 0 ? '—' : formatarReais(totalPdf)}
          </strong>
        </span>
      </div>
    </>
  );
}
